// Auth headers for same-origin /api calls.
//
// backend/public/auth-gate.js is loaded as a classic <script> in index.html, so
// it runs to completion before this (deferred) module — but it returns early to
// /login before publishing window.__ATLAS when there is no session, so every read
// of __ATLAS stays guarded.

interface PbGlobal {
  authHeader?: () => Record<string, string>;
  reauth?: () => boolean;
  token?: string;
  signOut?: () => void;
}

interface AtlasWindow extends Window {
  __ATLAS?: PbGlobal;
}

export function currentUser(): { username: string; initials: string } | null {
  const win = window as unknown as AtlasWindow;
  const token = win.__ATLAS?.token;
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const username: string = payload.preferred_username || payload.name || payload.email || 'user';
    const initials = username.slice(0, 2).toUpperCase();
    return { username, initials };
  } catch {
    return null;
  }
}

export function signOut(): void {
  const win = window as unknown as AtlasWindow;
  win.__ATLAS?.signOut?.();
}

export function pbAuthHeaders(extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = extra || {};
  try {
    const t =
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('atlas_id_token')) || '';
    if (t && !h.Authorization) h.Authorization = 'Bearer ' + t;
    const win = window as unknown as AtlasWindow;
    const a = win.__ATLAS && win.__ATLAS.authHeader ? win.__ATLAS.authHeader() : {};
    for (const k in a) h[k] = a[k];
  } catch {
    /* no-op */
  }
  return h;
}

// auth-gate refreshes the id token only at page load or when reauth() is called,
// so a token that expires mid-session keeps 401ing until someone asks for a new
// one. Retry once behind reauth() rather than surfacing that as a hard failure.
export async function pbAuthedFetch(
  url: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const opts = init || {};
  const send = () =>
    fetch(url, {
      ...opts,
      headers: pbAuthHeaders({ ...(opts.headers as Record<string, string> || {}) }),
    });
  const res = await send();
  if (res.status !== 401) return res;
  const win = window as unknown as AtlasWindow;
  const renewed =
    win.__ATLAS && typeof win.__ATLAS.reauth === 'function' ? win.__ATLAS.reauth() : false;
  return renewed ? send() : res;
}
